/**
 * Laboratorio de prompts (solo admin): lista de pruebas, detalle sondeado mientras corre y tabla de comparacion.
 * `draft` lleva un borrador del editor de prompts a "Nueva prueba" sin guardarlo.
 */
import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";

import { api } from "@/lib/api";
import type {
  Category,
  LabOutputKind,
  LabSummaryRow,
  LabTestResponse,
  LabTestSummary,
  PromptKey,
  PromptScope,
} from "@/lib/contracts";

export interface LabDraft {
  category: Category;
  promptSource: PromptScope;
  prompts: Partial<Record<PromptKey, string>>;
}

/** Texto que una prueba manda al editor ("Guardar borrador como versión"); el editor lo consume al abrir. */
export interface EditorSeed {
  scope: PromptScope;
  category: Category;
  key: PromptKey;
  text: string;
}

export const useLabStore = defineStore("lab", () => {
  const list = shallowRef<LabTestSummary[] | null>(null);
  const summary = shallowRef<LabSummaryRow[] | null>(null);
  const details = ref<Record<string, LabTestResponse>>({});
  const draft = ref<LabDraft | null>(null);
  const editorSeed = ref<EditorSeed | null>(null);

  async function refreshList(): Promise<LabTestSummary[]> {
    list.value = (await api.listLabTests()).tests;
    return list.value;
  }

  async function refreshSummary(): Promise<LabSummaryRow[]> {
    summary.value = (await api.getLabSummary()).rows;
    return summary.value;
  }

  async function refreshTest(id: string): Promise<LabTestResponse> {
    const res = await api.getLabTest(id);
    details.value = { ...details.value, [id]: res };
    return res;
  }

  async function rate(id: string, kind: LabOutputKind, score: number, note: string): Promise<LabTestResponse> {
    const res = await api.rateLabTest(id, kind, score, note);
    details.value = { ...details.value, [id]: res };
    summary.value = null;
    list.value = null;
    return res;
  }

  async function remove(id: string): Promise<void> {
    await api.deleteLabTest(id);
    details.value = Object.fromEntries(Object.entries(details.value).filter(([key]) => key !== id));
    if (list.value) list.value = list.value.filter((t) => t.test_id !== id);
    summary.value = null;
  }

  function forget(): void {
    list.value = null;
    summary.value = null;
  }

  function reset(): void {
    list.value = null;
    summary.value = null;
    details.value = {};
    draft.value = null;
    editorSeed.value = null;
  }

  return {
    list,
    summary,
    details,
    draft,
    editorSeed,
    refreshList,
    refreshSummary,
    refreshTest,
    rate,
    remove,
    forget,
    reset,
  };
});
